'use client';

import { ChangeEvent, useRef, useState } from 'react';
import { DocumentInterface, FileUploadProps } from '../../types/interfaces';
import AppButton from '../AppButton/AppButton';
import { toast } from 'react-toastify';
import { downloadDocument } from '../../services/edmsService';

export default function FileUpload({
  onFileSelect,
  onUpload = () => {},
  accept = '.jpeg,.jpg,.png,.pdf',
  maxSize = 4 * 1024 * 1024, // 4MB
  className,
  error,
  fileName,
  isViewOnly = false,
  showUpload = true,
  showNote = true,
  viewerText = 'View',
  documentFile,
  module = 'Supplier',
  subModule = null,
}: FileUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      if (file.size > maxSize) {
        toast.error('File size exceeds the limit');
        return;
      }
      setSelectedFile(file);
      onFileSelect(file);
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleViewDocument = async () => {
    if (!documentFile) return;
    const request = {
      documentId: documentFile.documentId,
      module,
      ...(subModule && { subModule }),
    };

    const response = await downloadDocument(request);
    if (response) {
      const fileExtension = documentFile.documentName.split('.').pop()?.toLowerCase();
      if (fileExtension === 'pdf') {
        // Open the PDF in a new tab
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');
        if (!newTab) {
          toast.error('Popup blocked. Please allow popups to view the document.');
        }
      } else {
        // Download the file
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', documentFile.documentName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else {
      toast.error(`Failed to ${viewerText.toLowerCase()} document`);
    }
  };

  return (
    <div>
      {!isViewOnly && (
        <div className={`space-y-2 ${className ? className : ''}`}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept={accept}
            className="hidden"
            aria-label="File upload input"
          />
          <div className="flex flex-wrap items-center gap-2 rounded-[5px] bg-[#F5F5F5]">
            <AppButton
              title="Choose File"
              onClick={handleChooseFile}
              className="cursor-pointer bg-white"
              outline
            />
            <span className="text-muted-foreground text-sm">
              {selectedFile ? selectedFile.name : 'No File Chosen'}
            </span>
            {showUpload && (
              <AppButton
                title="Upload"
                onClick={onUpload}
                disabled={!selectedFile}
                className="ml-auto rounded-[8px] bg-green-500 px-10 py-3 text-base text-white"
              />
            )}
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          {showNote && (
            <p className="text-muted-foreground text-sm">
              Please upload .jpeg, .jpg, .png or .pdf, size less than {maxSize / 1024 / 1024}
              MB
            </p>
          )}
        </div>
      )}

      <div>
        {isViewOnly && (
          <div className="space-y-4">
            <div className="">
              <div className="flex items-center justify-between rounded-lg bg-gray-100 px-4 py-3">
                <span className="truncate text-lg text-gray-700">{fileName}</span>
                <p
                  onClick={handleViewDocument}
                  className="mr-2 cursor-pointer text-lg text-green-600 underline"
                >
                  {viewerText}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
